import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ad_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Firm access revoked while already signed in — owners go to /pay, others back to /login
      if (status === 403 && detail && typeof detail === 'object' && detail.code === 'firm_locked' && path.startsWith('/dashboard')) {
        const u = (() => { try { return JSON.parse(localStorage.getItem('ad_user') || 'null'); } catch { return null; } })();
        const canPay = u && (u.firmRole === 'owner' || u.isPlatformAdmin);
        if (canPay) {
          window.location.href = '/pay';
          return Promise.reject(err);
        }
        try {
          sessionStorage.setItem('ad_lock_info', JSON.stringify({
            message: detail.message || 'Account access suspended.',
            contactEmail: detail.contactEmail || 'jpstevens13@gmail.com',
          }));
        } catch { /* ignore */ }
        localStorage.removeItem('ad_token');
        localStorage.removeItem('ad_user');
        window.location.href = '/login?locked=1';
        return Promise.reject(err);
      }
      if (status === 401 && path.startsWith('/dashboard')) {
        localStorage.removeItem('ad_token');
        localStorage.removeItem('ad_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  },
);

export const auth = {
  signup: (data) => api.post('/auth/signup', data).then((r) => r.data),
  login: (data) => api.post('/auth/login', data).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => {
    try { localStorage.setItem('ad_user', JSON.stringify(r.data)); } catch { /* ignore */ }
    return r.data;
  }),
  updateProfile: (data) => api.put('/auth/me', data).then((r) => r.data),
  acceptInvite: (data) => api.post('/auth/accept-invite', data).then((r) => r.data),
  getInvite: (code) => api.get(`/invites/${code}`).then((r) => r.data),
};

export const searchApi = {
  query: (q) => api.get('/search', { params: { q } }).then((r) => r.data),
};

export const notificationsApi = {
  list: () => api.get('/notifications').then((r) => r.data),
  markRead: (ids) => api.post('/notifications/read', { ids }).then((r) => r.data),
};

export const invoices = {
  list: () => api.get('/invoices').then((r) => r.data),
  get: (id) => api.get(`/invoices/${id}`).then((r) => r.data),
  create: (data) => api.post('/invoices', data).then((r) => r.data),
  update: (id, data) => api.put(`/invoices/${id}`, data).then((r) => r.data),
  pullTime: (id) => api.post(`/invoices/${id}/pull-time`).then((r) => r.data),
  setStatus: (id, status) => api.post(`/invoices/${id}/status`, { status }).then((r) => r.data),
  remove: (id) => api.delete(`/invoices/${id}`).then((r) => r.data),
  addPayment: (id, data) => api.post(`/invoices/${id}/payments`, data).then((r) => r.data),
  removePayment: (id, pid) => api.delete(`/invoices/${id}/payments/${pid}`).then((r) => r.data),
  send: (id) => api.post(`/invoices/${id}/send`).then((r) => r.data),
  emails: (id) => api.get(`/invoices/${id}/emails`).then((r) => r.data),
  pdfUrl: (id) => `${API_BASE}/invoices/${id}/pdf?auth=${encodeURIComponent(localStorage.getItem('ad_token') || '')}`,
  unbilledSummary: () => api.get('/billing/unbilled').then((r) => r.data),
};

export const billingApi = {
  settings: () => api.get('/billing/settings').then((r) => r.data),
  saveSettings: (data) => api.put('/billing/settings', data).then((r) => r.data),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/billing/settings/logo', fd).then((r) => r.data);
  },
  removeLogo: () => api.delete('/billing/settings/logo').then((r) => r.data),
  logoUrl: () => `${API_BASE}/billing/settings/logo?auth=${encodeURIComponent(localStorage.getItem('ad_token') || '')}&t=${Date.now()}`,
  setMemberRate: (mid, hourlyRate) => api.patch(`/team/members/${mid}/rate`, { hourlyRate }).then((r) => r.data),
};

export const templates = {
  list: () => api.get('/templates').then((r) => r.data),
  categories: () => api.get('/templates/categories').then((r) => r.data),
  get: (id) => api.get(`/templates/${id}`).then((r) => r.data),
  render: (id, clientId) => api.post(`/templates/${id}/render`, { clientId }).then((r) => r.data),
};

export const team = {
  list: () => api.get('/team').then((r) => r.data),
  invite: (data) => api.post('/team/invites', data).then((r) => r.data),
  cancelInvite: (id) => api.delete(`/team/invites/${id}`).then((r) => r.data),
  removeMember: (id) => api.delete(`/team/members/${id}`).then((r) => r.data),
  updateMember: (id, data) => api.patch(`/team/members/${id}`, data).then((r) => r.data),
};

export const clients = {
  list: () => api.get('/clients').then((r) => r.data),
  create: (data) => api.post('/clients', data).then((r) => r.data),
  update: (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/clients/${id}`).then((r) => r.data),
  rotatePortalToken: (id) => api.post(`/clients/${id}/portal/regenerate`).then((r) => r.data),
};

export const portal = {
  // Public — no auth. Uses raw axios to skip the interceptor's 401 redirect logic.
  get: (token) => axios.get(`${API_BASE}/portal/${token}`).then((r) => r.data),
  extras: (token) => axios.get(`${API_BASE}/portal/${token}/extras`).then((r) => r.data),
  uploadItem: (token, rid, iid, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API_BASE}/portal/${token}/documents/${rid}/items/${iid}/upload`, fd).then((r) => r.data);
  },
  fileUrl: (token, fid) => `${API_BASE}/portal/${token}/files/${fid}`,
  respond: (token, sid, body) => axios.post(`${API_BASE}/portal/${token}/signoffs/${sid}/respond`, body).then((r) => r.data),
};

export const clientService = {
  health: () => api.get('/clients/health').then((r) => r.data),
  docStatus: () => api.get('/documents/status').then((r) => r.data),
  documentsOverview: (period) => api.get('/documents/overview', { params: period ? { period } : {} }).then((r) => r.data),
  documents: (cid) => api.get(`/clients/${cid}/documents`).then((r) => r.data),
  updateChecklist: (cid, items) => api.put(`/clients/${cid}/documents/checklist`, { items }).then((r) => r.data),
  uploadItem: (cid, rid, iid, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/clients/${cid}/documents/${rid}/items/${iid}/upload`, fd).then((r) => r.data);
  },
  setItemStatus: (cid, rid, iid, status) => api.post(`/clients/${cid}/documents/${rid}/items/${iid}/status`, { status }).then((r) => r.data),
  fileUrl: (fid) => `${API_BASE}/client-files/${fid}?auth=${encodeURIComponent(localStorage.getItem('ad_token') || '')}`,
  reminders: (cid) => api.get(`/clients/${cid}/reminders`).then((r) => r.data),
  sendReminder: (cid) => api.post(`/clients/${cid}/reminders/send`).then((r) => r.data),
  signoffs: (cid) => api.get(`/clients/${cid}/signoffs`).then((r) => r.data),
  createSignoff: (cid, { title, description, file }) => {
    const fd = new FormData();
    fd.append('title', title);
    fd.append('description', description || '');
    if (file) fd.append('file', file);
    return api.post(`/clients/${cid}/signoffs`, fd).then((r) => r.data);
  },
  deleteSignoff: (sid) => api.delete(`/signoffs/${sid}`).then((r) => r.data),
  sarsAll: (status) => api.get('/sars', { params: status ? { status } : {} }).then((r) => r.data),
  sarsForClient: (cid) => api.get(`/clients/${cid}/sars`).then((r) => r.data),
  createSars: (body) => api.post('/sars', body).then((r) => r.data),
  updateSars: (id, body) => api.put(`/sars/${id}`, body).then((r) => r.data),
  deleteSars: (id) => api.delete(`/sars/${id}`).then((r) => r.data),
};

export const tasks = {
  list: () => api.get('/tasks').then((r) => r.data),
  archived: () => api.get('/tasks', { params: { archived: true } }).then((r) => r.data),
  archiveDone: () => api.post('/tasks/archive-done').then((r) => r.data),
  restore: (id) => api.post(`/tasks/${id}/restore`).then((r) => r.data),
  purgeArchived: () => api.delete('/tasks/archived').then((r) => r.data),
  create: (data) => api.post('/tasks', data).then((r) => r.data),
  update: (id, data) => api.put(`/tasks/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/tasks/${id}`).then((r) => r.data),
  startTimer: (id) => api.post(`/tasks/${id}/timer/start`).then((r) => r.data),
  stopTimer: (id) => api.post(`/tasks/${id}/timer/stop`).then((r) => r.data),
  activeTimer: () => api.get('/tasks/timer/active').then((r) => r.data),
};

export const timesheets = {
  current: (date) => api.get('/timesheets/current', { params: date ? { date } : {} }).then((r) => r.data),
  mine: () => api.get('/timesheets').then((r) => r.data),
  pending: () => api.get('/timesheets/pending').then((r) => r.data),
  firmApproved: () => api.get('/timesheets/firm-approved').then((r) => r.data),
  update: (id, data) => api.put(`/timesheets/${id}`, data).then((r) => r.data),
  submit: (id) => api.post(`/timesheets/${id}/submit`).then((r) => r.data),
  approve: (id) => api.post(`/timesheets/${id}/approve`).then((r) => r.data),
  reject: (id, note) => api.post(`/timesheets/${id}/reject`, { note }).then((r) => r.data),
};

export const chat = {
  list: (after) => api.get('/chat/messages', { params: after ? { after } : {} }).then((r) => r.data),
  send: (text, attachment) => api.post('/chat/messages', { text, attachment }).then((r) => r.data),
  remove: (id) => api.delete(`/chat/messages/${id}`).then((r) => r.data),
  upload: (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/chat/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    }).then((r) => r.data);
  },
  // Signed URL for <img src> / <a href>. Uses the ?auth=<jwt> fallback the backend accepts.
  fileUrl: (fileId) => `${API_BASE}/chat/files/${fileId}?auth=${encodeURIComponent(localStorage.getItem('ad_token') || '')}`,
};

export const deadlines = {
  list: () => api.get('/deadlines').then((r) => r.data),
};

export const dashboardApi = {
  overview: () => api.get('/dashboard').then((r) => r.data),
};

export const contact = {
  submit: (data) => api.post('/contact', data).then((r) => r.data),
};

export const financials = {
  overview: (months) => api.get('/financials/overview', { params: months ? { months } : {} }).then((r) => r.data),
  listExpenses: () => api.get('/financials/expenses').then((r) => r.data),
  createExpense: (data) => api.post('/financials/expenses', data).then((r) => r.data),
  updateExpense: (id, data) => api.put(`/financials/expenses/${id}`, data).then((r) => r.data),
  deleteExpense: (id) => api.delete(`/financials/expenses/${id}`).then((r) => r.data),
  listIncome: () => api.get('/financials/income').then((r) => r.data),
  createIncome: (data) => api.post('/financials/income', data).then((r) => r.data),
  updateIncome: (id, data) => api.put(`/financials/income/${id}`, data).then((r) => r.data),
  deleteIncome: (id) => api.delete(`/financials/income/${id}`).then((r) => r.data),
  categories: () => api.get('/financials/categories').then((r) => r.data),
};

export const payments = {
  plans: () => api.get('/payments/plans').then((r) => r.data),
  initialize: (plan) => api.post('/payments/paystack/initialize', {
    plan,
    origin_url: window.location.origin,
  }).then((r) => r.data),
  verify: (reference) => api.get(`/payments/paystack/verify/${reference}`).then((r) => r.data),
  history: () => api.get('/payments/history').then((r) => r.data),
};

export const teamPermissions = {
  update: (userId, permissions) => api.patch(`/team/members/${userId}/permissions`, { permissions }).then((r) => r.data),
};

export const admin = {
  listFirms: () => api.get('/admin/firms').then((r) => r.data),
  listMembers: (firmId) => api.get(`/admin/firms/${firmId}/members`).then((r) => r.data),
  lock: (firmId, reason) => api.post(`/admin/firms/${firmId}/lock`, { reason }).then((r) => r.data),
  unlock: (firmId) => api.post(`/admin/firms/${firmId}/unlock`).then((r) => r.data),
  activate: (firmId) => api.post(`/admin/firms/${firmId}/activate`).then((r) => r.data),
  extendTrial: (firmId, days) => api.post(`/admin/firms/${firmId}/extend-trial`, { days }).then((r) => r.data),
  setNotes: (firmId, notes) => api.post(`/admin/firms/${firmId}/notes`, { notes }).then((r) => r.data),
  deleteFirm: (firmId) => api.delete(`/admin/firms/${firmId}`).then((r) => r.data),
  deleteMember: (firmId, userId) => api.delete(`/admin/firms/${firmId}/members/${userId}`).then((r) => r.data),
};

export function saveSession({ token, user }) {
  localStorage.setItem('ad_token', token);
  localStorage.setItem('ad_user', JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem('ad_token');
  localStorage.removeItem('ad_user');
}

export function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('ad_user') || 'null'); } catch { return null; }
}

export default api;

const authQ = () => `auth=${encodeURIComponent(localStorage.getItem('ad_token') || '')}`;
export const payroll = {
  clients: () => api.get('/payroll/clients').then((r) => r.data),
  enable: (cid, enabled) => api.post(`/payroll/clients/${cid}/enable`, { enabled }).then((r) => r.data),
  employees: (cid) => api.get(`/payroll/clients/${cid}/employees`).then((r) => r.data),
  createEmployee: (cid, data) => api.post(`/payroll/clients/${cid}/employees`, data).then((r) => r.data),
  updateEmployee: (eid, data) => api.put(`/payroll/employees/${eid}`, data).then((r) => r.data),
  deleteEmployee: (eid) => api.delete(`/payroll/employees/${eid}`).then((r) => r.data),
  runs: (cid) => api.get(`/payroll/clients/${cid}/runs`).then((r) => r.data),
  createRun: (cid, data) => api.post(`/payroll/clients/${cid}/runs`, data).then((r) => r.data),
  run: (rid) => api.get(`/payroll/runs/${rid}`).then((r) => r.data),
  saveInputs: (rid, inputs) => api.put(`/payroll/runs/${rid}/inputs`, { inputs }).then((r) => r.data),
  finalise: (rid) => api.post(`/payroll/runs/${rid}/finalise`).then((r) => r.data),
  reopen: (rid) => api.post(`/payroll/runs/${rid}/reopen`).then((r) => r.data),
  deleteRun: (rid) => api.delete(`/payroll/runs/${rid}`).then((r) => r.data),
  emailPayslips: (rid) => api.post(`/payroll/runs/${rid}/email`).then((r) => r.data),
  emp201: (cid, taxYear) => api.get(`/payroll/clients/${cid}/emp201`, { params: { taxYear } }).then((r) => r.data),
  emp501: (cid, taxYear, period) => api.get(`/payroll/clients/${cid}/emp501`, { params: { taxYear, period } }).then((r) => r.data),
  payslipsPdf: (rid, eid) => `${API_BASE}/payroll/runs/${rid}/payslips.pdf?${eid ? `employeeId=${eid}&` : ''}${authQ()}`,
  emp201Pdf: (cid, month) => `${API_BASE}/payroll/clients/${cid}/emp201/${month}.pdf?${authQ()}`,
  emp501Pdf: (cid, y, p) => `${API_BASE}/payroll/clients/${cid}/emp501.pdf?taxYear=${y}&period=${p}&${authQ()}`,
  emp501Csv: (cid, y, p) => `${API_BASE}/payroll/clients/${cid}/emp501.csv?taxYear=${y}&period=${p}&${authQ()}`,
  irp5Pdf: (cid, eid, y, p) => `${API_BASE}/payroll/clients/${cid}/irp5/${eid}.pdf?taxYear=${y}&period=${p}&${authQ()}`,
};

export const payrollTakeOn = {
  get: (cid, taxYear) => api.get(`/payroll/clients/${cid}/takeon`, { params: { taxYear } }).then((r) => r.data),
  save: (eid, data) => api.put(`/payroll/employees/${eid}/takeon`, data).then((r) => r.data),
  remove: (eid, taxYear) => api.delete(`/payroll/employees/${eid}/takeon`, { params: { taxYear } }).then((r) => r.data),
  saveEmp201: (cid, taxYear, months) => api.put(`/payroll/clients/${cid}/takeon-emp201`, { taxYear, months }).then((r) => r.data),
  templateUrl: (cid, taxYear) => `${API_BASE}/payroll/clients/${cid}/takeon-template.csv?taxYear=${taxYear}&${authQ()}`,
  importCsv: (cid, taxYear, file) => {
    const fd = new FormData();
    fd.append('taxYear', taxYear);
    fd.append('file', file);
    return api.post(`/payroll/clients/${cid}/takeon-import`, fd).then((r) => r.data);
  },
};

export const payrollLeave = {
  get: (cid, asAt) => api.get(`/payroll/clients/${cid}/leave`, { params: { asAt } }).then((r) => r.data),
  add: (cid, data) => api.post(`/payroll/clients/${cid}/leave`, data).then((r) => r.data),
  remove: (lid) => api.delete(`/payroll/leave/${lid}`).then((r) => r.data),
  workdays: (start, end, perWeek) => api.get('/payroll/leave/workdays', { params: { start, end, perWeek } }).then((r) => r.data),
};

const tokenQ = () => `auth=${encodeURIComponent(localStorage.getItem('ad_token') || '')}`;
export const prospects = {
  meta: () => api.get('/prospects/meta').then((r) => r.data),
  list: () => api.get('/prospects').then((r) => r.data),
  get: (id) => api.get(`/prospects/${id}`).then((r) => r.data),
  create: (data) => api.post('/prospects', data).then((r) => r.data),
  update: (id, data) => api.put(`/prospects/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/prospects/${id}`).then((r) => r.data),
  stage: (id, stage, lostReason = '') => api.post(`/prospects/${id}/stage`, { stage, lostReason }).then((r) => r.data),
  regenerateLink: (id) => api.post(`/prospects/${id}/link/regenerate`).then((r) => r.data),
  saveInfo: (id, info) => api.put(`/prospects/${id}/info`, info).then((r) => r.data),
  prepare: (id, keys) => api.post(`/prospects/${id}/documents/prepare`, { keys }).then((r) => r.data),
  editDoc: (id, did, data) => api.put(`/prospects/${id}/documents/${did}`, data).then((r) => r.data),
  removeDoc: (id, did) => api.delete(`/prospects/${id}/documents/${did}`).then((r) => r.data),
  send: (id) => api.post(`/prospects/${id}/documents/send`).then((r) => r.data),
  docPdfUrl: (id, did) => `${API_BASE}/prospects/${id}/documents/${did}/pdf?${tokenQ()}`,
  fileUrl: (fid) => `${API_BASE}/client-files/${fid}?${tokenQ()}`,
  saveFica: (id, items) => api.put(`/prospects/${id}/fica`, { items }).then((r) => r.data),
  uploadFica: (id, iid, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/prospects/${id}/fica/${iid}/upload`, fd).then((r) => r.data); },
  ficaStatus: (id, iid, status) => api.post(`/prospects/${id}/fica/${iid}/status`, { status }).then((r) => r.data),
  convert: (id, override = false) => api.post(`/prospects/${id}/convert`, { override }).then((r) => r.data),
  templates: () => api.get('/prospects/templates').then((r) => r.data),
  saveTemplate: (key, data) => api.put(`/prospects/templates/${key}`, data).then((r) => r.data),
  resetTemplate: (key) => api.delete(`/prospects/templates/${key}`).then((r) => r.data),
};

export const onboard = {
  get: (t) => axios.get(`${API_BASE}/onboard/${t}`).then((r) => r.data),
  logoUrl: (t) => `${API_BASE}/onboard/${t}/logo`,
  saveInfo: (t, info) => axios.put(`${API_BASE}/onboard/${t}/info`, info).then((r) => r.data),
  sign: (t, body) => axios.post(`${API_BASE}/onboard/${t}/sign`, body).then((r) => r.data),
  upload: (t, iid, file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API_BASE}/onboard/${t}/fica/${iid}/upload`, fd).then((r) => r.data); },
  docPdfUrl: (t, did) => `${API_BASE}/onboard/${t}/documents/${did}/pdf`,
  fileUrl: (t, fid) => `${API_BASE}/onboard/${t}/files/${fid}`,
};

export const retainers = {
  list: () => api.get('/retainers').then((r) => r.data),
  run: () => api.post('/retainers/run').then((r) => r.data),
  markPaid: (iid, paid) => api.post(`/retainers/invoices/${iid}/paid`, { paid }).then((r) => r.data),
};

